import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AssetWritePayload, MarkAssetSoldPayload } from "../services/engineApi";
import { trailerApi } from "../services/trailerApi";

export const trailerKeys = {
  all: ["trailers"] as const,
  lists: () => [...trailerKeys.all, "list"] as const,
  list: (filters: Record<string, any>) => [...trailerKeys.lists(), filters] as const,
  details: () => [...trailerKeys.all, "detail"] as const,
  detail: (id: string) => [...trailerKeys.details(), id] as const,
};

/**
 * Fetch the trailer list. CRITICAL: pass a MEMOIZED filters object from the
 * caller (useMemo) to avoid infinite re-renders, mirroring useBoats.
 */
export function useTrailers(filters: Record<string, any>, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: trailerKeys.list(filters),
    queryFn: () => trailerApi.getTrailers(filters),
    enabled: options?.enabled ?? true,
  });
}

/** Fetch a single trailer (+ tagged-boat history), mirroring useBoat. */
export function useTrailer(id: string) {
  return useQuery({
    queryKey: trailerKeys.detail(id),
    queryFn: () => trailerApi.getTrailer(id),
    enabled: !!id,
  });
}

/** SP2-D mutations — see useEngines for the invalidation rationale. */
function useTrailerWriteInvalidation() {
  const queryClient = useQueryClient();

  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: trailerKeys.lists() });
    if (id) queryClient.invalidateQueries({ queryKey: trailerKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: ["boats"] });
  };
}

export function useCreateTrailer() {
  const invalidate = useTrailerWriteInvalidation();

  return useMutation({
    mutationFn: (data: AssetWritePayload) => trailerApi.createTrailer(data),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateTrailer(id: string) {
  const invalidate = useTrailerWriteInvalidation();

  return useMutation({
    mutationFn: (data: AssetWritePayload) => trailerApi.updateTrailer(id, data),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteTrailer() {
  const invalidate = useTrailerWriteInvalidation();

  return useMutation({
    mutationFn: (id: string) => trailerApi.deleteTrailer(id),
    onSuccess: () => invalidate(),
  });
}

/** SP4 slice 1 — mark-sold. 422s with errors.active_link while attached (D2). */
export function useMarkTrailerSold(id: string) {
  const invalidate = useTrailerWriteInvalidation();

  return useMutation({
    mutationFn: (data: MarkAssetSoldPayload) => trailerApi.markSold(id, data),
    onSuccess: () => invalidate(id),
  });
}
